import { ReactNode, memo } from 'react';
import { Box, Typography, SxProps } from '@mui/material';

export type InfoItemVariant = 'filled' | 'outlined';
export type InfoItemDirection = 'horizontal' | 'vertical';
export type InfoItemStatus = 'success' | 'error' | 'default';

export interface InfoItemProps {
    label: string;
    labelId?: string;
    value?: string | ReactNode;
    children?: ReactNode;
    variant?: InfoItemVariant;
    direction?: InfoItemDirection;
    status?: InfoItemStatus;
    sx?: SxProps;
}

const InfoItem: React.FC<InfoItemProps> = ({
    label,
    labelId,
    value,
    children,
    variant = 'outlined',
    direction = 'vertical',
    status = 'default',
    sx = {},
}) => {
    const isHorizontal = direction === 'horizontal';
    const isFilled = variant === 'filled';

    const getBackgroundColor = () => {
        if (!isFilled) return 'transparent';
        return status === 'default' ? 'secondary.light' : status === 'success' ? 'success.light' : 'error.light';
    };

    const containerSx: SxProps = {
        display: 'flex',
        width: '100%',
        borderRadius: 1,
        ...(isHorizontal
            ? {
                  flexDirection: 'row',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  gap: 0.25,
                  p: 0.5,
                  px: 1,
              }
            : {
                  flexDirection: 'column',
                  gap: 0.5,
                  p: 1,
              }),
        ...(isFilled
            ? {
                  backgroundColor: getBackgroundColor(),
              }
            : {
                  border: '1px solid',
                  borderColor: 'devblue.10',
              }),
        ...sx,
    };

    const labelSx: SxProps = {
        variant: 'body2',
        color: 'text.primary',
        ...(isHorizontal
            ? {
                  whiteSpace: 'nowrap',
                  minWidth: '80px',
              }
            : {
                  mb: 0.5,
                  whiteSpace: 'nowrap',
              }),
    };

    const valueSx: SxProps = {
        variant: 'body2',
        fontWeight: 500,
        ...(isHorizontal
            ? {
                  wordBreak: 'break-word',
                  textAlign: 'right',
                  flex: 'none',
                  color: 'text.primary.dark',
              }
            : {
                  color: 'text.primary',
              }),
    };

    const renderContent = () => {
        if (children) {
            return <Box sx={{ flex: 1, minWidth: 0 }}>{children}</Box>;
        }

        if (value) {
            return (
                <Typography variant="body2" sx={valueSx} aria-labelledby={labelId}>
                    {value}
                </Typography>
            );
        }

        return null;
    };

    return (
        <Box sx={containerSx}>
            <Typography variant="body2" sx={labelSx} id={labelId}>
                {label}
            </Typography>
            {renderContent()}
        </Box>
    );
};

export default memo(InfoItem);
